/**
 * @zep/feature-flag
 *
 * Vendor-agnostic feature flag package for ZEP Client.
 * Internally powered by Flipt, but consumers only interact
 * with a generic abstraction layer.
 *
 * ## Bucketing Modes
 *
 * 모든 훅과 컴포넌트는 `bucketBy` (required)를 받습니다:
 * - `'user'`   — userId 기준 (cross-device 일관성, 로그인 유저 대상 A/B 테스트)
 * - `'device'` — deviceId 기준 (로그인/로그아웃에도 안정적, 회원가입 퍼널 등)
 *
 * ## Domain Hooks / Components (권장 패턴)
 *
 * 같은 플래그를 여러 곳에서 사용할 때, bucketBy와 context를 매번 반복하지 않도록
 * 도메인 훅/컴포넌트로 캡슐화하는 것을 권장합니다:
 *
 * ```ts
 * export function useExperimentFlag() {
 *   return useFeatureFlag('experiment-key', { bucketBy: 'user' });
 * }
 *
 * export function ExperimentFlagGate({ children, fallback }) {
 *   return (
 *     <FeatureFlag flagKey="experiment-key" bucketBy="user" fallback={fallback}>
 *       {children}
 *     </FeatureFlag>
 *   );
 * }
 * ```
 *
 * 이 패턴은 필수가 아니지만, 플래그 설정을 한 곳에서 관리하고
 * 사용처에서는 도메인 의미만 드러나도록 해줍니다.
 */

// --- Types ---
export type { FlagKey } from './registry';
export type {
  FlagMetadata,
  ServerSideFlagRequest,
  ServerSideFeatureFlagConfig,
  ServerSideFeatureFlagResult,
} from './types';

// --- Registry ---
export { FLAG_KEYS, FLAG_METADATA, FLAG_METADATA_MAP } from './registry';

// --- React (client-side, WASM via dynamic import in createFliptClient.ts) ---
export { FeatureFlagProvider } from './client/FeatureFlagProvider';
export type { BucketBy } from './client/FeatureFlagContext';
export { FeatureFlag } from './client/FeatureFlag';
export type { FeatureFlagProps } from './client/FeatureFlag';
export { FeatureVariant } from './client/FeatureVariant';
export type { FeatureVariantProps } from './client/FeatureVariant';
export { useFeatureFlag } from './client/useFeatureFlag';
export type { UseFeatureFlagOptions } from './client/useFeatureFlag';
export { useFeatureVariant } from './client/useFeatureVariant';
export type { UseFeatureVariantOptions } from './client/useFeatureVariant';

// --- Utilities ---
export { mapStageToEnvironment } from './utils/environment';
